import * as React from "react";
import { Keys } from "~/common/enums";

interface Options {
    key?: Keys;
    handler: F1<React.KeyboardEvent, any>;
    handlePrevented?: boolean;
}

/**
 * Creates callback that might be assigned to onKeyUp/onKeyDown property
 * of html element to handle particular key pressing.
 * By default this callback will ignore default prevented event (see handlePrevented option).
 * @param opts
 * @param deps (specify dependencies if you need memoized version of callback).
 */
export const useKeyboardHandler = (opts: Options, deps?: React.DependencyList) => {
    const handler: Handler = e => {
        if (opts.key !== undefined && opts.key === e.key && (!e.isDefaultPrevented() || opts.handlePrevented)) {
            opts.handler(e);
        }
    };
    return deps ? React.useCallback(handler, deps) : handler;
};

const useKeyHandler = (key?: Keys) => (handler: Options["handler"], deps?: React.DependencyList) => {
    return useKeyboardHandler(
        {
            key,
            handler,
        },
        deps
    );
};

export const useEnterKeyHandler = useKeyHandler(Keys.Enter);
export const useEscapeKeyHandler = useKeyHandler(Keys.Escape);
export const useTabKeyHandler = useKeyHandler(Keys.Tab);

type Handler = React.KeyboardEventHandler;
export const composeHandlers =
    (...handlers: Handler[]): Handler =>
    e =>
        handlers.forEach(h => h(e));
